/**
 * Profile Page ViewModel
 *
 * STRICT MVVM PATTERN - ALL business logic in ViewModel
 * Returns: { state, actions, computed, onMount }
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  fetchProfileAsync,
  updateUsernameAsync,
  uploadAvatarAsync,
  deleteAvatarAsync,
  addWalletAsync,
  deleteWalletAsync,
  toggleWalletVisibilityAsync,
  addSocialLinkAsync,
  deleteSocialLinkAsync,
  toggleSocialLinkVisibilityAsync,
  exportUserDataAsync,
  deleteAccountAsync,
  selectProfile,
  selectProfileLoading,
  selectProfileError,
  selectUploadProgress,
  selectIsExporting,
  selectIsDeleting,
} from '@/store/slices/profileSlice';
import { signOutLocallyAsync } from '@/store/slices/authSlice';
import type { UserProfile } from '@domain/ports/IProfileGateway';

export interface ProfileViewModelState {
  profile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  uploadProgress: number;
  isExporting: boolean;
  isDeleting: boolean;
  showExportModal: boolean;
  showDeleteModal: boolean;
}

export interface ProfileViewModelActions {
  updateUsername: (username: string) => Promise<void>;
  uploadAvatar: (file: File) => Promise<void>;
  deleteAvatar: () => Promise<void>;
  addWallet: (type: 'ETH' | 'SOL' | 'BTC', address: string, ensName?: string) => Promise<void>;
  deleteWallet: (type: 'ETH' | 'SOL' | 'BTC') => Promise<void>;
  toggleWalletVisibility: (type: 'ETH' | 'SOL' | 'BTC') => void;
  addSocialLink: (platform: string, url: string) => Promise<void>;
  deleteSocialLink: (platform: string) => Promise<void>;
  toggleSocialLinkVisibility: (platform: string) => void;
  refreshProfile: () => Promise<void>;
  // Export Data Modal Actions
  openExportModal: () => void;
  closeExportModal: () => void;
  confirmExport: () => Promise<void>;
  // Delete Account Modal Actions
  openDeleteModal: () => void;
  closeDeleteModal: () => void;
  confirmDelete: () => Promise<void>;
}

export interface ProfileViewModelComputed {
  hasAvatar: boolean;
  walletCount: number;
  socialLinkCount: number;
  canAddWallet: (type: 'ETH' | 'SOL' | 'BTC') => boolean;
  canAddSocialLink: (platform: string) => boolean;
  initials: string;
}

export interface ProfileViewModel {
  state: ProfileViewModelState;
  actions: ProfileViewModelActions;
  computed: ProfileViewModelComputed;
  onMount: () => () => void;
}

/**
 * useProfileViewModel Hook
 *
 * All business logic for Profile page
 * React Compiler automatically memoizes functions
 */
export function useProfileViewModel(): ProfileViewModel {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const profile = useAppSelector(selectProfile);
  const isLoading = useAppSelector(selectProfileLoading);
  const error = useAppSelector(selectProfileError);
  const uploadProgress = useAppSelector(selectUploadProgress);
  const isExporting = useAppSelector(selectIsExporting);
  const isDeleting = useAppSelector(selectIsDeleting);

  // Local modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // State
  const state: ProfileViewModelState = {
    profile,
    isLoading,
    error,
    uploadProgress,
    isExporting,
    isDeleting,
    showExportModal,
    showDeleteModal,
  };

  // Actions
  const actions: ProfileViewModelActions = {
    updateUsername: async (username: string) => {
      // Check if username existed before
      const hadUsername = profile?.username && profile.username.trim() !== '';
      try {
        await dispatch(updateUsernameAsync({ username })).unwrap();
        // Refetch profile to get updated data
        await dispatch(fetchProfileAsync());
        toast.success(t(hadUsername ? 'profile.success.usernameUpdated' : 'profile.success.usernameAdded'));
      } catch (error) {
        // Re-throw error for ProfilePage to handle inline error display
        throw error;
      }
    },

    uploadAvatar: async (file: File) => {
      // Check if avatar existed before
      const hadAvatar = Boolean(profile?.avatar);
      await dispatch(uploadAvatarAsync(file)).unwrap();
      // Refetch profile to get updated data
      await dispatch(fetchProfileAsync());
      toast.success(t(hadAvatar ? 'profile.success.avatarUpdated' : 'profile.success.avatarAdded'));
    },

    deleteAvatar: async () => {
      await dispatch(deleteAvatarAsync()).unwrap();
      toast.success(t('profile.success.avatarDeleted'));
    },

    addWallet: async (type: 'ETH' | 'SOL' | 'BTC', address: string, ensName?: string) => {
      console.log('[ViewModel] addWallet called', { type, address, ensName });
      // Check if wallet of this type existed before
      const hadWallet = profile?.wallets.some(w => w.type === type);
      await dispatch(addWalletAsync({ type, address, ensName })).unwrap();
      console.log('[ViewModel] addWallet completed');
      // No refetch needed - optimistic update handles it
      toast.success(t(hadWallet ? 'profile.success.walletUpdated' : 'profile.success.walletAdded'));
    },

    deleteWallet: async (type: 'ETH' | 'SOL' | 'BTC') => {
      await dispatch(deleteWalletAsync({ type })).unwrap();
      // Refetch profile to get updated wallets
      await dispatch(fetchProfileAsync());
      toast.success(t('profile.success.walletDeleted'));
    },

    toggleWalletVisibility: (type: 'ETH' | 'SOL' | 'BTC') => {
      // Optimistic update - no await
      dispatch(toggleWalletVisibilityAsync({ type }));
      toast.success(t('profile.success.walletVisibilityUpdated'));
    },

    addSocialLink: async (platform: string, url: string) => {
      // Check if social link for this platform existed before
      const hadLink = profile?.socialLinks.some(link => link.platform === platform);
      await dispatch(addSocialLinkAsync({ platform, url })).unwrap();
      // No refetch needed - optimistic update handles it
      toast.success(t(hadLink ? 'profile.success.socialLinkUpdated' : 'profile.success.socialLinkAdded'));
    },

    deleteSocialLink: async (platform: string) => {
      await dispatch(deleteSocialLinkAsync({ platform })).unwrap();
      // Refetch profile to get updated social links
      await dispatch(fetchProfileAsync());
      toast.success(t('profile.success.socialLinkDeleted'));
    },

    toggleSocialLinkVisibility: (platform: string) => {
      // Optimistic update - no await
      dispatch(toggleSocialLinkVisibilityAsync({ platform }));
      toast.success(t('profile.success.socialLinkVisibilityUpdated'));
    },

    refreshProfile: async () => {
      await dispatch(fetchProfileAsync()).unwrap();
    },

    // Export Data Modal Actions
    openExportModal: () => {
      setShowExportModal(true);
    },

    closeExportModal: () => {
      setShowExportModal(false);
    },

    confirmExport: async () => {
      try {
        await dispatch(exportUserDataAsync()).unwrap();
        setShowExportModal(false);
        toast.success(t('profile.exportSuccess'));
      } catch {
        toast.error(t('profile.exportError', 'Failed to request data export'));
      }
    },

    // Delete Account Modal Actions
    openDeleteModal: () => {
      setShowDeleteModal(true);
    },

    closeDeleteModal: () => {
      setShowDeleteModal(false);
    },

    confirmDelete: async () => {
      try {
        await dispatch(deleteAccountAsync()).unwrap();
        setShowDeleteModal(false);
        toast.success(t('profile.deleteSuccess'));
        // Clear local auth state without triggering Cognito federated logout
        // (user was already deleted from Cognito by the backend)
        await dispatch(signOutLocallyAsync());
        navigate('/');
      } catch {
        toast.error(t('profile.deleteError', 'Failed to delete account'));
      }
    },
  };

  // Computed properties
  const computed: ProfileViewModelComputed = {
    hasAvatar: Boolean(profile?.avatar),

    walletCount: profile?.wallets.length || 0,

    socialLinkCount: profile?.socialLinks.length || 0,

    canAddWallet: (type: 'ETH' | 'SOL' | 'BTC') => {
      if (!profile) return false;
      // Max one wallet per type
      return !profile.wallets.some((w) => w.type === type);
    },

    canAddSocialLink: (platform: string) => {
      if (!profile) return false;
      // Max one link per platform
      return !profile.socialLinks.some((s) => s.platform === platform);
    },

    initials: (() => {
      if (!profile?.username) return '';
      const parts = profile.username.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return profile.username.slice(0, 2).toUpperCase();
    })(),
  };

  // Lifecycle
  const onMount = useCallback(() => {
    dispatch(fetchProfileAsync());

    return () => {
      // Cleanup if needed
    };
  }, [dispatch]);

  return { state, actions, computed, onMount };
}
